import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef, useState } from 'react';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';

const testimonials = [
  {
    name: "Sarah M.",
    location: "United States",
    text: "This journey changed my life in ways I never imagined possible. Jonatas created such a safe and sacred space. The combination of ancient wisdom and professional care was exactly what I needed.",
    rating: 5
  },
  {
    name: "Marcus K.",
    location: "Germany",
    text: "I came seeking answers and found so much more. The setting in the Amazon, the genuine care from the team, and the profound ceremonies helped me reconnect with my true self.",
    rating: 5
  },
  {
    name: "Elena R.",
    location: "Spain",
    text: "The attention to safety and preparation was impressive. I felt completely supported throughout my journey. This was the most transformative experience of my life.",
    rating: 5
  },
  {
    name: "James L.",
    location: "Australia",
    text: "Coming from the other side of the world was absolutely worth it. The retreat exceeded all expectations. Professional, authentic, and deeply healing.",
    rating: 5
  },
  {
    name: "Aiko T.",
    location: "Japan",
    text: "The integration support after the ceremony was invaluable. Months later, I'm still processing the insights and implementing positive changes in my life.",
    rating: 5
  }
];

export const TestimonialsSection = () => {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [currentIndex, setCurrentIndex] = useState(0);

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="py-24 relative overflow-hidden" data-testid="testimonials-section">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-neon-magenta/5 rounded-full blur-[200px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <p className="font-accent text-neon-magenta text-sm tracking-[0.3em] mb-4">
            VOICES OF TRANSFORMATION
          </p>
          <h2 className="font-heading font-bold text-4xl sm:text-5xl md:text-6xl uppercase tracking-tight text-white mb-6">
            What <span className="neon-gold">Travelers</span> Say
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          {/* Main Testimonial Card */}
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.4 }}
            className="testimonial-card relative"
            data-testid={`testimonial-card-${currentIndex}`}
          >
            {/* Quote Icon */}
            <div className="absolute -top-6 left-8">
              <div className="w-12 h-12 rounded-full bg-egyptian-gold flex items-center justify-center">
                <Quote className="w-6 h-6 text-jungle-dark" />
              </div>
            </div>

            <div className="pt-8">
              {/* Stars */}
              <div className="flex gap-1 mb-6">
                {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-egyptian-gold" fill="currentColor" />
                ))}
              </div>

              {/* Testimonial Text */}
              <p className="font-body text-gray-300 text-lg leading-relaxed mb-8 italic">
                "{testimonials[currentIndex].text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold text-white text-lg uppercase tracking-wide">
                    {testimonials[currentIndex].name}
                  </p>
                  <p className="font-body text-neon-cyan text-sm">
                    {testimonials[currentIndex].location}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Navigation Arrows */}
          <div className="flex justify-center gap-4 mt-8">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full glass-card flex items-center justify-center border border-neon-cyan/30 hover:border-neon-cyan transition-colors duration-300"
              data-testid="testimonial-prev-btn"
            >
              <ChevronLeft className="w-6 h-6 text-neon-cyan" />
            </button>
            
            {/* Dots */}
            <div className="flex items-center gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    index === currentIndex ? 'bg-egyptian-gold' : 'bg-gray-600'
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full glass-card flex items-center justify-center border border-neon-cyan/30 hover:border-neon-cyan transition-colors duration-300"
              data-testid="testimonial-next-btn"
            >
              <ChevronRight className="w-6 h-6 text-neon-cyan" />
            </button>
          </div>
        </div>

        {/* Mini testimonials grid for larger screens */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="hidden lg:grid grid-cols-3 gap-6 mt-16"
        >
          {testimonials.slice(0, 3).map((testimonial, index) => (
            <div
              key={index}
              className="glass-card rounded-xl p-6 border border-white/5 hover:border-neon-cyan/20 transition-colors duration-300"
              data-testid={`mini-testimonial-${index}`}
            >
              <div className="flex gap-1 mb-3">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 text-egyptian-gold" fill="currentColor" />
                ))}
              </div>
              <p className="font-body text-gray-400 text-sm leading-relaxed mb-4 line-clamp-3">
                "{testimonial.text}"
              </p>
              <p className="font-heading text-white text-sm uppercase">
                {testimonial.name} · <span className="text-neon-cyan font-normal">{testimonial.location}</span>
              </p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
